import { Checkbox, FormControlLabel, ListItemIcon, MenuItem, Select, Stack, TextField, Typography } from "@mui/material"
import "./AddEditExceptionEntryModal.styles.css";
import { AddEditExceptionEntryModalProps } from "./AddEditExceptionEntryModal.props";
import InputLabel from "@mui/material/InputLabel";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EXCEPTION_CATEGORY_NAMES, EXCEPTION_TYPE_NAMES } from "../../../constants/contants";
import { OverriddenValue } from "../../../pages/ThreatExceptions";
import { TagInput, TagInputTypes } from "../../atoms/TagInput";
import { AlertMessage } from "../../atoms/AlertMessage";
import CheckIcon from "@mui/icons-material/Check";

const BannerContent: React.FC<any> = ({ type = '' }) => {
  const example = type === EXCEPTION_TYPE_NAMES.domain || type === EXCEPTION_TYPE_NAMES.sender_domain
    ? '*phriendlyphishing.com'
    : type === EXCEPTION_TYPE_NAMES.url
      ? 'https://phriendlyphishing.com/*learning/' : '*john@mail.com';
  return (
    <Typography variant="body1" component="div">
      <strong>Use a wildcard (*) before/after a value</strong> to match any string that contains that text.
      Example: <strong>{example}</strong>
    </Typography>
  )
};

const MAX_TAGS = 10;
const MAX_LENGTHS = {
  [TagInputTypes.URL]: 2083,
  [TagInputTypes.DOMAIN]: 254,
  [TagInputTypes.SENDER_DOMAIN]: 254,
  [TagInputTypes.EMAIL]: 254,
  [TagInputTypes.DNS]: 254,
  [TagInputTypes.IP]: 45,
  [TagInputTypes.FILEHASH]: 64,
};
const MAX_NOTES_LEN = 255;

export const AddEditExceptionEntryModal: React.FC<AddEditExceptionEntryModalProps> = (
  {
    isEdit,
    formData,
    entryValueTags,
    tagsChangeHandler,
    tagRef,
    tagInputError,
    setTagInputError,
  }
) => {
  const [tagInputValue, setTagInputValue] = useState('');
  const { t } = useTranslation();

  const { values: formValues, errors: formErrors, touched: formTouched, handleChange, handleBlur, handleSubmit } = formData;
  const { type, category, notes, value, status } = formValues;
  const { notes: notesTouched, value: valueTouched } = formTouched;
  const { notes: notesError, value: valueError } = formErrors;

  useEffect(() => {
    setTagInputValue('');
    setTagInputError?.(false);
    formData.validateForm();
    if (tagRef?.current) {
      tagRef.current.value = '';
    }
  }, [type]);

  const bannerIsVisible = [TagInputTypes.DOMAIN, TagInputTypes.EMAIL, TagInputTypes.URL, TagInputTypes.SENDER_DOMAIN].includes(type as TagInputTypes);

  const onNotesChange = React.useCallback(
    (e) => {
      if (e.target.value.length <= MAX_NOTES_LEN) handleChange(e);
    },
    [handleChange]
  );

  return (
    <Stack id="exception-entry-modal-form">
      <Stack>
        <form onSubmit={handleSubmit}>
          <Stack direction="row" gap={3} paddingBottom={3}>
            <Stack className="input-container" gap={0.25}>
              <InputLabel className={'input-label'}>
                {t('fields.threatExceptions.typeInput')}<span style={{ color: 'red', marginLeft: 6 }}>*</span>
              </InputLabel>
              <Select
                value={type}
                onChange={handleChange}
                name="type"
                renderValue={(selected) => EXCEPTION_TYPE_NAMES[selected]}
              >
                {Object.keys(EXCEPTION_TYPE_NAMES).map((typeItem, index) => typeItem !== 'custom' && (
                  <MenuItem
                    key={typeItem + index}
                    value={typeItem}
                  >
                    {EXCEPTION_TYPE_NAMES[typeItem]}
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {type === typeItem && (
                        <CheckIcon fontSize="small"/>
                      )}
                    </ListItemIcon>
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            <Stack className="input-container" gap={0.25}>
              <InputLabel className="input-label">
                {t('fields.threatExceptions.categoryInput')}<span style={{ color: 'red', marginLeft: 6 }}>*</span>
              </InputLabel>
              <Select
                value={category}
                onChange={handleChange}
                name="category"
                renderValue={(selected: string) => <OverriddenValue value={selected}/>}
              >
                {Object.keys(EXCEPTION_CATEGORY_NAMES).map((categoryItem, index) => (
                  <MenuItem
                    key={categoryItem + index}
                    value={categoryItem}
                  >
                    <OverriddenValue value={categoryItem}/>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      {categoryItem === category && (
                        <CheckIcon fontSize="small"/>
                      )}
                    </ListItemIcon>
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Stack>
          <Stack border={bannerIsVisible ? '1px solid #E1E0E0' : ''} padding={bannerIsVisible ? 2 : 0}
                 paddingBottom={bannerIsVisible ? 2.5 : 0} gap={2}>
            <Stack className="input-container full-width">
              {
                bannerIsVisible &&
                  <AlertMessage content={<BannerContent type={EXCEPTION_TYPE_NAMES[type]} category={EXCEPTION_CATEGORY_NAMES[category]}/>}
                                severity="info" roundWarningIcon/>
              }
              <InputLabel className="input-label">
                {t(`fields.threatExceptions.value${isEdit ? '' : 's'}`)}
                <span style={{ color: 'red', marginLeft: 6 }}>*</span>
              </InputLabel>
              {isEdit
                ? <TextField
                  name="value"
                  value={value}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  helperText={valueError || ((valueTouched && value.length >= MAX_LENGTHS[type]) && t('validation.charExceeded', {
                    field: EXCEPTION_TYPE_NAMES[type === 'sender_domain' ? 'domain' : type],
                    max: MAX_LENGTHS[type]
                  }))}
                  error={!!valueError || (valueTouched && value.length >= MAX_LENGTHS[type])}
                  inputProps={{ maxLength: MAX_LENGTHS[type] }}
                />
                : <TagInput
                  tags={entryValueTags}
                  handleModifyTags={tagsChangeHandler}
                  tagRef={tagRef}
                  type={type as TagInputTypes}
                  placeholder={t('messages.threatExceptions.valuesPlaceholder')}
                  errorMsg={(tagInputError && !tagInputValue.trim()) ? t('validation.required', { field: 'Values' }) : t(`errors.threatExceptions.${type}`)}
                  error={tagInputError}
                  setError={setTagInputError}
                  inputRows={4}
                  maxTags={MAX_TAGS}
                  maxInputLength={MAX_LENGTHS[type] || 0}
                  inputValue={tagInputValue}
                  setInputValue={setTagInputValue}
                />
              }
            </Stack>
          </Stack>
          <Stack className="input-container full-width" width="100%" paddingTop={3}>
            <InputLabel className="input-label">
              {t('fields.threatExceptions.notes')}<span style={{ color: 'red', marginLeft: 6 }}>*</span>
            </InputLabel>
            <TextField
              name="notes"
              placeholder={t('messages.threatExceptions.notes')}
              onBlur={handleBlur}
              onChange={onNotesChange}
              inputProps={{ maxLength: MAX_NOTES_LEN }}
              id="notes-input"
              helperText={notesTouched && (notesError || (notes.length >= MAX_NOTES_LEN && t('validation.maxChar', {
                max: MAX_NOTES_LEN,
              })))}
              error={notesTouched && (!!notesError || notes.length >= MAX_NOTES_LEN)}
              value={notes}
              multiline
              rows={4}
            />
          </Stack>
          <Stack paddingTop={3} className="input-container">
            <InputLabel className="input-label">
              {t('fields.templates.status')}
            </InputLabel>
            <FormControlLabel
              control={
                <Checkbox
                  data-testid={'ckBoxStatus'}
                  name={'status'}
                  id={'status'}
                  checked={status}
                  onChange={handleChange}
                  color="success"/>
              }
              style={{ width: "fit-content" }}
              label={"Active"}
            />
          </Stack>
        </form>
      </Stack>
    </Stack>
  )
};
